import { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Image,
  Text,
  Heading,
  SimpleGrid,
  Spinner,
  Tooltip,
  useColorModeValue,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  HiEye,
  HiHome,
  HiOutlinePlus,
  HiOutlineCog,
} from "react-icons/hi";

import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import { useAuthenticatedRequest } from "./hooks/useAuthenticatedRequest";

const Designer = () => {
  const navigate = useNavigate();
  const { get } = useAuthenticatedRequest();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);

  const [documentDefinitions, setDocumentDefinitions] = useState([]);
  const [documentDefinitionsLoading, setDocumentDefinitionsLoading] = useState(true);

  const [smartLedgers, setSmartLedgers] = useState([]);
  const [smartLedgersLoading, setSmartLedgersLoading] = useState(true);

  const cardBg = useColorModeValue("white", "gray.700");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await get("https://poc.mrc.ai/api/products");
        setProducts(response.data);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchRegisters = async () => {
      try {
        const res = await get("https://registers.poc.mrc.ai/api/registers/headers");
        setRegisters(res.data);
      } catch (err) {
        console.error("Failed to fetch registers:", err);
      } finally {
        setRegisterLoading(false);
      }
    };

    const fetchDocumentDefinitions = async () => {
      try {
        const res = await get("https://documents.poc.mrc.ai/api/definitions/headers");
        setDocumentDefinitions(res.data);
      } catch (err) {
        console.error("Failed to fetch document definitions:", err);
      } finally {
        setDocumentDefinitionsLoading(false);
      }
    };

    const fetchSmartLedgers = async () => {
      try {
        const res = await get("https://ledgers.poc.mrc.ai/api/smartledgers/headers");
        setSmartLedgers(res.data);
      } catch (err) {
        console.error("Failed to fetch smart ledgers:", err);
      } finally {
        setSmartLedgersLoading(false);
      }
    };

    fetchProducts();
    fetchRegisters();
    fetchDocumentDefinitions();
    fetchSmartLedgers();
  }, []);

  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Box flexShrink={0}>
        <Navbar />
      </Box>

      <Box
        position="relative"
        width="100%"
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={1} display="flex" flexDirection="column" height="100%">
          <Tabs
            orientation="horizontal"
            variant="line"
            colorScheme="linkedin"
            isLazy={true}
            lazyBehavior="unmount"
            display="flex"
            flexDirection="column"
            height="100%">
            <TabList>
              <Tab>Products</Tab>
              <Tab>Registers</Tab>
              <Tab>Document Definitions</Tab>
              <Tab>Smart Ledgers</Tab>
            </TabList>
            <TabPanels flex="1" overflow="auto">
              {/* Products Tab */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  {loading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {products.map((product) => (
                        <WrapItem key={product._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            minW="250px"
                            maxW="320px"
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack
                              align="center"
                              justify="flex-start"
                              mb={2}
                              spacing={1}>
                              <Tooltip label="Go to the Product Dashboard">
                                <IconButton
                                  icon={<HiHome />}
                                  size="md"
                                  onClick={() =>
                                    navigate(`/product/${product.code}`)
                                  }
                                  aria-label="Go to product"
                                />
                              </Tooltip>
                              <Heading
                                size="sm"
                                textAlign="left"
                                mb={0}
                                noOfLines={1}>
                                {product.name}
                              </Heading>
                              <Text as="span" fontSize="sm" color="gray.500">
                                ({product.code})
                              </Text>
                            </HStack>
                            <Text
                              fontSize="xs"
                              color="gray.700"
                              mb={1}
                              noOfLines={1}>
                              {product.description}
                            </Text>

                            <SimpleGrid>
                              <Box
                                borderWidth="1px"
                                borderRadius="md"
                                borderColor="gray.200">
                                <Tooltip label="Edit Product Definition">
                                  <IconButton
                                    icon={<HiOutlineCog />}
                                    size="md"
                                    onClick={() =>
                                      navigate(
                                        `/poc/product/${product.code}/edit`,
                                        {
                                          state: { isNew: false },
                                        }
                                      )
                                    }
                                    aria-label="Go to product"
                                  />
                                </Tooltip>
                              </Box>
                            </SimpleGrid>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>

              {/* Registers Tab */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                    mb={2}
                    borderColor={"gray.500"}
                    backgroundColor="whiteAlpha.800">
                    <HStack>
                      <Tooltip label="Add register">
                        <IconButton
                          icon={<HiOutlinePlus />}
                          aria-label="Add register"
                          size="md"
                          onClick={() => navigate("/registers/new")}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {registerLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {registers.map((register) => (
                        <WrapItem key={register._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit Register...">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Edit register"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/registers/${register._id}`)
                                  }
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {register.name}
                              </Heading>
                            </HStack>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>

              {/* Document Definitions Tab */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  mb={2}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flexShrink={0}>
                  <HStack>
                    <Tooltip label="Add Document Definition">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        aria-label="Add Document Definition"
                        size="md"
                        onClick={() => navigate("/documentdefinitions/new")}
                      />
                    </Tooltip>
                  </HStack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  {documentDefinitionsLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {documentDefinitions.map((documentDefinition) => (
                        <WrapItem key={documentDefinition._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit Document Definition...">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Edit document definition"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/documentdefinitions/${documentDefinition._id}`
                                    )
                                  }
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {documentDefinition.name}
                              </Heading>
                            </HStack>

                            {documentDefinition.segments.map((segment) => (
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                mt={2}
                                key={segment.name}>
                                {segment.name}
                              </Text>
                            ))}
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>

              {/* Smart Ledgers Tab */}
              <TabPanel height="100%" display="flex" flexDirection="column" p={2}>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={1}
                  mb={2}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flexShrink={0}>
                  <HStack>
                    <Tooltip label="Add Smart Ledger">
                      <IconButton
                        icon={<HiOutlinePlus />}
                        aria-label="Add Smart Ledger"
                        size="md"
                        onClick={() => navigate("/smartledgers/new")}
                      />
                    </Tooltip>
                  </HStack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor={"gray.500"}
                  backgroundColor="whiteAlpha.800"
                  flex="1"
                  overflow="auto">
                  {smartLedgersLoading ? (
                    <Spinner />
                  ) : (
                    <Wrap spacing={4}>
                      {smartLedgers.map((smartLedger) => (
                        <WrapItem key={smartLedger._id}>
                          <Box
                            bg={cardBg}
                            p={4}
                            rounded="lg"
                            shadow="md"
                            border="1px solid"
                            borderColor="gray.300"
                            minW="200px"
                            maxW="fit-content"
                            transition="all 0.2s ease"
                            _hover={{
                              shadow: "lg",
                              transform: "translateY(-4px)",
                            }}>
                            <HStack>
                              <Tooltip label="Edit Smart Ledger...">
                                <IconButton
                                  icon={<HiEye />}
                                  aria-label="Edit smart ledger"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/smartledgers/${smartLedger._id}`)
                                  }
                                />
                              </Tooltip>
                              <Heading size="sm" mb={0} noOfLines={1}>
                                {smartLedger.name}
                              </Heading>
                            </HStack>
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>
    </Box>
  );
};

export default Designer;
